import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { Button, Tabs, Tab, AppBar, Badge, Paper, FormControl, Select, MenuItem, CircularProgress } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Heading from "../../Heading.js";
import StaffActRosterTable from "./StaffActRosterTable";
import StaffInvitedActsTable from "./StaffInvitedActsTable";
import StaffActApplicationsTable from "./StaffActApplicationsTable";
import StaffActRosterInvitationModal from "./StaffActRosterInvitationModal.js";
import StaffActRosterToolbar from "./StaffActRosterToolbar";
import StaffActPaginationToolbar from "./StaffActPaginationToolbar";
import Loading from "../../loadingSpinner/Loading";
import { ProfileRelationType } from "../../../types/enum";
import "./StaffActRoster.css";
import agent from "../../../agent";

const styles = theme => ({
    tabBadge: {
        padding: "0 8px"
    },
    badge: {
        top: "50%",
        right: -16,
        backgroundColor: "#ff9800",
        color: "white"
    },
    badgeHighlight: {
        top: "50%",
        right: -16,
        backgroundColor: "#f44336",
        color: "white",
        animation: "pulse 2s infinite"
    }
});

@inject("venueActStore", "templateStore", "userInvitationStore", "profileStore", "lookupStore")
@withRouter
@observer
class StaffActRosterContainer extends Component {
    state = {
        invitationModalState: false,
        isLoading: true,
        tabValue: 0,
        selectedVenueId: "all",
        actTypes: [],
        vibes: [],
        catalogsLoading: true,
        catalogsError: false,
        startupError: "",
        filters: {
            searchTerm: "",
            actTypeFilter: "all",
            lineupFilter: "all",
            selectedVibes: [],
            selectedGenres: []
        }
    };

    constructor(props) {
        super(props);
        this.props.venueActStore.resetList();
        this.props.venueActStore.startStaffRosterQuery();
        this.props.userInvitationStore.resetInvitations();
    }

    componentDidMount() {
        this.isMountedRoster = true;
        this.loadCatalogs();
        this.setState(
            {
                ...this.state,
                isLoading: true
            },
            () => {
                this.props.profileStore.loadProfiles().then(() => {
                    if (!this.isMountedRoster) return;
                    return Promise.all([
                        this.props.userInvitationStore.getMyUserInvitation(ProfileRelationType.VenueAct.ordinal),
                        this.props.venueActStore.getStaffPendingActRoster(),
                        this.props.venueActStore.loadStaffRoster()
                    ]).then(() => {
                        if (this.isMountedRoster) this.setState({ isLoading: false });
                    });
                }).catch(() => {
                    if (this.isMountedRoster) this.setState({
                        isLoading: false, startupError: "Could not load roster invitations."
                    });
                });
            }
        );

        if (this.props.match?.params?.tab) {
            const tab = this.props.match.params.tab;
            if (tab === "roster" || tab === "default") {
                this.setState({ tabValue: 0 });
            } else if (tab === "invited") {
                this.setState({ tabValue: 1 });
            } else if (tab === "applications") {
                this.setState({ tabValue: 2 });
            }
        }
    }

    loadCatalogs = () => {
        this.setState({ catalogsLoading: true, catalogsError: false });
        return Promise.all([
            agent.ActType.getAll(),
            agent.Vibe.getAll(),
            this.props.lookupStore.getGenreSuggestions().catch(() => [])
        ])
            .then(([actTypes, vibes, genres]) => {
                // checking (act type, vibe & genre) is not ObservableArray
                const isValid = Array.isArray(actTypes) &&
                    Array.isArray(vibes) &&
                    (Array.isArray(genres) || (genres && typeof genres.map === 'function'));

                if (!isValid) throw new Error("Invalid filter catalog");

                if (this.isMountedRoster) this.setState({ actTypes, vibes });
            })
            .catch(() => {
                if (this.isMountedRoster) this.setState({ catalogsError: true });
            })
            .finally(() => {
                if (this.isMountedRoster) this.setState({ catalogsLoading: false });
            });
    };

    componentWillUnmount() {
        this.isMountedRoster = false;
        clearTimeout(this.searchTimer);
        this.props.venueActStore.stopStaffRosterQuery();
    }

    reloadRoster = () => this.props.venueActStore.loadStaffRoster(this.props.profileStore.getCurrentProfile()?.id, 1);

    openInvitationModal = () => {
        this.setState({ ...this.state, invitationModalState: true });
    };

    closeInvitationModal = () => {
        this.setState({ ...this.state, invitationModalState: false });
    };

    handleTabChange = (event, value) => {
        switch (value) {
            case 0:
                this.props.history.replace("/staff/acts");
                break;
            case 1:
                this.props.history.replace("/staff/acts/invited");
                break;
            case 2:
                this.props.history.replace("/staff/acts/applications");
                break;
            default:
                this.props.history.replace("/staff/acts");
                break;
        }
        this.setState({ tabValue: value });
    };

    handleVenueChange = (event) => {
        clearTimeout(this.searchTimer);
        this.props.venueActStore.setStaffRosterVenue(event.target.value);
        this.setState({ selectedVenueId: event.target.value });
        return this.reloadRoster();
    };

    handleFiltersChange = (filters) => {
        const searchChanged = filters.searchTerm !== this.state.filters.searchTerm;
        clearTimeout(this.searchTimer);
        this.props.venueActStore.setStaffRosterCriteria({
            search: filters.searchTerm,
            actTypeId: filters.actTypeFilter === "all" ? "" : filters.actTypeFilter,
            lineupCountId: filters.lineupFilter === "all" ? "" : filters.lineupFilter,
            vibeIds: filters.selectedVibes,
            genreIds: filters.selectedGenres
        });
        this.setState({ filters });
        if (searchChanged && filters.searchTerm.trim()) {
            this.searchTimer = setTimeout(this.reloadRoster, 500);
        } else {
            this.reloadRoster();
        }
    };

    handlePageChange = (page) => {
        return this.props.venueActStore.loadStaffRoster(this.props.profileStore.getCurrentProfile()?.id, page);
    };

    handlePageSizeChange = (size) => {
        clearTimeout(this.searchTimer);
        this.props.venueActStore.setStaffPageSize(size);
        return this.reloadRoster();
    };

    handleLoadAll = () => {
        return this.props.venueActStore.loadStaffRoster(this.props.profileStore.getCurrentProfile()?.id, 1, true);
    };

    getVenueFilteredRosters = () => {
        return this.props.venueActStore.venueActRoster || [];
    };

    getFilteredRosters = () => {
        // Staff-wide and venue-scoped backend pages apply the complete criteria.
        return this.getVenueFilteredRosters();
    };

    getPaginatedRosters = () => {
        return this.getFilteredRosters();
    };

    getUniqueVenues = () => {
        const { staffRosterVenues } = this.props.venueActStore;
        const venues = [];
        const seenIds = new Set();
        
        if (staffRosterVenues && staffRosterVenues.length > 0) {
            staffRosterVenues.forEach(venue => {
                if (venue && venue.id && !seenIds.has(venue.id)) {
                    seenIds.add(venue.id);
                    venues.push(venue);
                }
            });
        }
        return venues;
    };

    getInvitedCount = () => {
        const { invitations } = this.props.userInvitationStore;
        return invitations.filter(inv => !inv.additionalData).length;
    };

    getApplicationsCount = () => {
        const { invitations } = this.props.userInvitationStore;
        const { pendingRequest } = this.props.venueActStore;
        const invitationApps = invitations.filter(inv => inv.additionalData).length;
        const pendingApps = pendingRequest ? pendingRequest.length : 0;
        return invitationApps + pendingApps;
    };

    renderTabComponent = () => {
        const { tabValue, selectedVenueId } = this.state;
        const { venueActStore } = this.props;
        const { currentPage, pageSize, allLoaded, isMore } = venueActStore.staffPagination;
        const currentProfile = this.props.profileStore.getCurrentProfile();
        const venueFilteredRosters = this.getVenueFilteredRosters();
        const filteredRosters = this.getFilteredRosters();
        const paginatedRosters = this.getPaginatedRosters();

        switch (tabValue) {
            case 0:
                return (
                    <Fragment>
                        <StaffActRosterToolbar
                            filters={this.state.filters}
                            actTypes={this.state.actTypes}
                            vibes={this.state.vibes}
                            genres={this.props.lookupStore.genres}
                            lineups={Array.from({ length: 99 }, (_, i) => ({
                                id: i + 1, name: venueActStore.getLineUpCounts(i + 1)
                            }))}
                            disabled={this.state.isLoading}
                            catalogsLoading={this.state.catalogsLoading}
                            catalogsError={this.state.catalogsError}
                            onRetryCatalogs={this.loadCatalogs}
                            totalCount={venueFilteredRosters.length}
                            filteredCount={filteredRosters.length}
                            onFiltersChange={this.handleFiltersChange}
                        />
                        <StaffActRosterTable 
                            allowBooking={currentProfile?.allowBooking} 
                            selectedVenueId={selectedVenueId}
                            paginatedRosters={paginatedRosters}
                            genreCatalog={this.props.lookupStore.genres}
                        />
                        {venueActStore.staffRosterError && (
                            <div role="alert">
                                {venueActStore.staffRosterError}
                                <Button size="small" onClick={() => venueActStore.retryStaffRoster(currentProfile?.id)}>Retry</Button>
                            </div>
                        )}
                        {venueActStore.staffPagination.loadingAll && (
                            <Button onClick={() => venueActStore.cancelStaffLoadAll()}>Cancel Load All</Button>
                        )}
                        {!this.state.isLoading && (
                            <StaffActPaginationToolbar
                                currentPage={currentPage}
                                pageSize={pageSize}
                                totalItems={filteredRosters.length}
                                isMore={isMore}
                                disabled={venueActStore.staffRosterLoading}
                                isAllLoaded={allLoaded}
                                onPageChange={this.handlePageChange}
                                onPageSizeChange={this.handlePageSizeChange}
                                onLoadAll={this.handleLoadAll}
                            />
                        )}
                    </Fragment>
                );
            case 1:
                return (
                    <StaffInvitedActsTable 
                        allowBooking={currentProfile?.allowBooking}
                        selectedVenueId={selectedVenueId}
                    />
                );
            case 2:
                return (
                    <StaffActApplicationsTable 
                        allowBooking={currentProfile?.allowBooking}
                        selectedVenueId={selectedVenueId}
                    />
                );
            default:
                return null;
        }
    };

    render() {
        const { classes } = this.props;
        const currentProfile = this.props.profileStore.getCurrentProfile();
        const invitedCount = this.getInvitedCount();
        const applicationsCount = this.getApplicationsCount();
        const venues = this.getUniqueVenues();
        const selectedVenueMissing = this.state.selectedVenueId !== "all" &&
            !venues.some(venue => String(venue.id) === String(this.state.selectedVenueId));
        const showVenueFilter = venues.length > 0 || this.state.selectedVenueId !== "all";

        return (
            <Fragment>
                <Heading title="My Act Roster">
                    {!(currentProfile?.viewOnly) && currentProfile?.allowBooking && (
                        <Button 
                            color="primary" 
                            variant="contained" 
                            onClick={this.openInvitationModal} 
                            classes={{ root: "pull-right" }}
                        >
                            Add Act
                        </Button>
                    )}
                </Heading>

                {this.props.venueActStore.staffVenueOptionsLoading && <div role="status">Loading venue choices...</div>}
                {this.props.venueActStore.staffVenueOptionsError && (
                    <div role="alert">
                        {this.props.venueActStore.staffVenueOptionsError}
                        <Button size="small" onClick={() => this.props.venueActStore.retryStaffVenueOptions(currentProfile?.id)}>
                            Retry venue choices
                        </Button>
                    </div>
                )}
                {showVenueFilter && (
                    <div className="staff-venue-filter">
                        <span className="filter-label">
                            <i className="fa fa-building"></i> Viewing acts for:
                        </span>
                        <FormControl className="venue-select-control">
                            <Select
                                value={this.state.selectedVenueId}
                                onChange={this.handleVenueChange}
                                className="venue-select"
                            >
                                <MenuItem value="all">All My Venues ({venues.length})</MenuItem>
                                {selectedVenueMissing && (
                                    <MenuItem value={this.state.selectedVenueId} disabled>Selected venue unavailable</MenuItem>
                                )}
                                {venues.map(venue => (
                                    <MenuItem key={venue.id} value={String(venue.id)}>
                                        {venue.profileName || venue.fullName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}

                <Paper style={{ position: "relative" }} className="staff-act-roster-paper">
                    <Loading showed={this.state.isLoading} borderRadius={0} />
                    {this.props.venueActStore.staffRosterLoading && (
                        <div role="status"><CircularProgress size={14} /> Loading acts...</div>
                    )}
                    {this.state.startupError && <div role="alert">{this.state.startupError}</div>}
                    
                    <AppBar position="static" elevation={2} className="staff-roster-tabs-bar">
                        <Tabs
                            value={this.state.tabValue}
                            onChange={this.handleTabChange}
                            indicatorColor="primary"
                            textColor="secondary"
                            scrollable
                            scrollButtons="off"
                            classes={{
                                root: "tabs-root",
                                indicator: "tabs-indicator"
                            }}
                        >
                            <Tab
                                value={0}
                                label="Active Roster"
                                classes={{ selected: "tab-selected" }}
                            />
                            <Tab
                                value={1}
                                label={
                                    <span className={classes.tabBadge}>
                                        Invited Acts
                                        {invitedCount > 0 && (
                                            <Badge
                                                badgeContent={invitedCount}
                                                color="primary"
                                                classes={{ badge: classes.badge }}
                                                style={{ marginLeft: 20 }}
                                            />
                                        )}
                                    </span>
                                }
                                classes={{ selected: "tab-selected" }}
                            />
                            <Tab
                                value={2}
                                label={
                                    <span className={classes.tabBadge}>
                                        Applications
                                        {applicationsCount > 0 && (
                                            <Badge
                                                badgeContent={applicationsCount}
                                                color="secondary"
                                                classes={{ badge: classes.badgeHighlight }}
                                                style={{ marginLeft: 20 }}
                                            />
                                        )}
                                    </span>
                                }
                                classes={{ selected: "tab-selected" }}
                            />
                        </Tabs>
                    </AppBar>

                    {this.renderTabComponent()}
                </Paper>

                <StaffActRosterInvitationModal 
                    open={this.state.invitationModalState} 
                    onClose={this.closeInvitationModal} 
                />
            </Fragment>
        );
    }
}

export default withStyles(styles)(StaffActRosterContainer);
